import { __decorate } from "tslib";
import React from 'react';
import { findDOMNode } from 'react-dom';
import noop from 'lodash/noop';
import { action, observable, runInAction } from 'mobx';
import Trigger from '../../../lib/trigger/Trigger';
import { Action } from '../../../lib/trigger/enum';
import { getIf } from '../data-set/utils';
import { TextField } from '../text-field/TextField';
import autobind from '../_util/autobind';
import Icon from '../icon';
import TaskRunner from '../_util/TaskRunner';
import BUILT_IN_PLACEMENTS from './placements';
import { TriggerViewMode } from './enum';
import { hide } from '../tooltip/singleton';
export { TriggerViewMode, };
export default class TriggerField extends TextField {
    constructor(props, context) {
        super(props, context);
        runInAction(() => {
            this.statePopup = false;
        });
    }
    get popup() {
        return this.statePopup;
    }
    saveTrigger(node) {
        this.trigger = node;
    }
    isValidationMessageHidden(message) {
        return super.isValidationMessageHidden(message) || this.popup;
    }
    setPopup(statePopup) {
        const { trigger = this.getContextConfig('selectTrigger') } = this.props;
        if (statePopup !== this.statePopup) {
            this.statePopup = statePopup;
            const { onPopupHiddenChange = noop } = this.props;
            onPopupHiddenChange(!statePopup);
            if (statePopup && !(trigger.includes(Action.hover))) {
                hide();
            }
        }
    }
    getRootDomNode() {
        return findDOMNode(this);
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'popupContent',
            'popupCls',
            'popupStyle',
            'popupPlacement',
            'editable',
            'trigger',
            'triggerShowDelay',
            'triggerHiddenDelay',
            'onPopupHiddenChange',
            'getPopupContainer',
            'getPopupAlignTarget',
            'extraFooterPlacement',
            'renderExtraFooter',
            'treeDefaultExpandedKeys',
            'treeDefaultExpandAll',
            'tabIntoPopupContent',
            'viewMode',
        ]);
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            viewMode: props.viewMode,
        };
    }
    getPopupProps() {
        return {
            setValue: this.setValue.bind(this),
            setPopup: this.setPopup.bind(this),
            content: this.getPopupContent(),
        };
    }
    getPopupClassName(defaultClassName) {
        return defaultClassName;
    }
    getPopupWrapper() {
        const { trigger } = this;
        if (trigger) {
            return trigger.getPopupWrapper();
        }
    }
    renderPopupContent() {
        const { popupContent } = this.props;
        if (popupContent === undefined) {
            return this.getPopupContent();
        }
        if (popupContent instanceof Function) {
            return popupContent(this.getPopupProps());
        }
        return popupContent;
    }
    getDefaultAction() {
        return [Action.focus, Action.click];
    }
    getWrappedEditor(renderedValue) {
        const { prefixCls, props: { popupCls, popupStyle, popupPlacement, hidden, trigger = this.getDefaultAction(), triggerShowDelay, triggerHiddenDelay, getPopupContainer, tabIntoPopupContent, getPopupAlignTarget = this.getRootDomNode, }, } = this;
        return (React.createElement(Trigger, { ref: this.saveTrigger, action: this.readOnly || this.disabled ? [] : trigger, focusDelay: triggerShowDelay, blurDelay: triggerHiddenDelay, mouseEnterDelay: triggerShowDelay, mouseLeaveDelay: triggerHiddenDelay, prefixCls: prefixCls, popupCls: this.getPopupClassName(popupCls), popupStyle: popupStyle, popupContent: this.renderPopupContent, popupPlacement: popupPlacement, popupHidden: hidden || !this.popup, builtinPlacements: BUILT_IN_PLACEMENTS, onPopupAnimateAppear: this.handlePopupAnimateAppear, onPopupAnimateEnd: this.handlePopupAnimateEnd, onPopupHiddenChange: this.handlePopupHiddenChange, getPopupStyleFromAlign: this.getPopupStyleFromAlign, getRootDomNode: getPopupAlignTarget, getPopupContainer: getPopupContainer, tabIntoPopupContent: tabIntoPopupContent, childrenProps: renderedValue, getContextConfig: this.getContextConfig }, this.getEditor));
    }
    getWrapperClassNames(...args) {
        const { prefixCls } = this;
        return super.getWrapperClassNames(...args, {
            [`${prefixCls}-expand`]: this.popup,
            [`${prefixCls}-not-editable`]: !this.disabled && !this.editable,
        });
    }
    getDefaultSuffix() {
        const { prefixCls } = this;
        return React.createElement(Icon, { type: this.getTriggerIconFont(), className: `${prefixCls}-trigger` });
    }
    handleTagAnimateEnd() {
        this.forcePopupAlign();
    }
    handlePopupMouseDown(e) {
        e.preventDefault();
        const { onMouseDown = noop } = this.props;
        onMouseDown(e);
    }
    handlePopupHiddenChange(hidden) {
        this.setPopup(!hidden);
    }
    forcePositionChanged() {
        this.forcePopupAlign();
    }
    forcePopupAlign() {
        if (this.trigger) {
            this.trigger.forcePopupAlign();
        }
    }
    expand() {
        const popupTask = getIf(this, 'popupTask', () => new TaskRunner());
        popupTask.cancel();
        if (!this.readOnly && !this.popup) {
            popupTask.delay(this.props.triggerShowDelay, () => {
                this.setPopup(true);
            });
        }
    }
    collapse() {
        const popupTask = getIf(this, 'popupTask', () => new TaskRunner());
        popupTask.cancel();
        if (!this.readOnly && this.popup) {
            const { element } = this;
            // 如果当前焦点在popup中，将焦点换给输入框
            let triggerHiddenDelay = this.props.triggerHiddenDelay;
            if (this.isFocused && element !== document.activeElement) {
                element.focus();
                const { triggerShowDelay } = this.props;
                triggerHiddenDelay += triggerShowDelay;
            }
            popupTask.delay(triggerHiddenDelay, () => {
                this.setPopup(false);
            });
        }
    }
}
TriggerField.displayName = 'TriggerField';
TriggerField.defaultProps = {
    ...TextField.defaultProps,
    suffixCls: 'trigger',
    clearButton: true,
    popupPlacement: 'bottomLeft',
    triggerShowDelay: 150,
    triggerHiddenDelay: 50,
    viewMode: TriggerViewMode.popup,
};
__decorate([
    observable
], TriggerField.prototype, "statePopup", void 0);
__decorate([
    autobind
], TriggerField.prototype, "saveTrigger", null);
__decorate([
    autobind
], TriggerField.prototype, "isValidationMessageHidden", null);
__decorate([
    action
], TriggerField.prototype, "setPopup", null);
__decorate([
    autobind
], TriggerField.prototype, "getRootDomNode", null);
__decorate([
    autobind
], TriggerField.prototype, "getPopupWrapper", null);
__decorate([
    autobind
], TriggerField.prototype, "renderPopupContent", null);
__decorate([
    autobind
], TriggerField.prototype, "handleTagAnimateEnd", null);
__decorate([
    autobind
], TriggerField.prototype, "handlePopupMouseDown", null);
__decorate([
    autobind
], TriggerField.prototype, "handlePopupHiddenChange", null);
